export function init() {
	const constants = {
		EASING: "easeOutSine",
		DURATION: 1000,
		animationStatus: {
			COMPLETED: "completed",
			PAUSED: "paused",
			RUNNING: "running",
		},
		MIN_SCROLL_INPUT: 25,
	}

	const tween = (animation, duration, callback) => {
		return anime({
			...animation,
			duration,
			update: callback,
		})
	}

	class TimelineAnimation {
		constructor(el) {
			this.tl = anime.timeline({
				direction: 'normal',
				autoplay: false,
				loop: false,
				targets: el,
			});
			this.animation = {
				progress: 0,
			}
			this.isAnimating = false;
			this.previousScrollPosition = 0;
			this.currentAnimation = null;
			this.animationExecutor = null;
			this.executionCondition = () => true;
			this._scrollDirection = "down";
		}

		get scrollDirection() {
			return this._scrollDirection;
		}

		set scrollDirection(direction) {
			this._scrollDirection = direction;
		}

		getAnimation(index) {
			const animation = this.tl.children[index];

			const animationObject = {
				targets: this.animation,
				progress: animation.timelineOffset + animation.duration,
				easing: constants.EASING,
				begin: () => {
					this.isAnimating = true;
				},
				complete: () => {
					this.isAnimating = false;
				}
			}

			return {
				play: () => {
					this.pauseCurrentAnimation()
					this.currentAnimation = tween(animationObject, constants.DURATION, () => {
						this.seek(this.animation.progress);
					})
				},
			}
		}

		executeAnimation(animation, condition) {
			this.executionCondition = condition.bind(this, this);
			this.animationExecutor = animation;

			return this;
		}

		add(...args) {
			this.tl.add(...args);

			return this;
		}

		seek(time) {
			this.tl.seek(time);
		}

		seekOnScroll(event) {
			if (this.animationExecutor && this.executionCondition(this)) {
				this.animationExecutor.play();
			}

			if (this.isAnimating) return;

			const isScrollingDown = this.isScrollingDown(event);
			const scroll = window.scrollY;
			const duration = this.tl.duration;
			const factor = 1.5;
			const easing = 'easeOutQuad';
			const differenceFactor = constants.DURATION / 2;

			if (this.animation.progress > this.tl.duration) {
				this.animation.progress = this.tl.duration;
				return;
			}

			const findNearestAnimation = () => {
				let nearestAnimtion = this.tl.children[0];
				let minDifference = Math.abs(this.animation.progress - nearestAnimtion.timelineOffset);

				this.tl.children.forEach(animation => {
					const difference = Math.abs(this.animation.progress - animation.timelineOffset);

					if (difference < minDifference) {
						minDifference = difference;
						nearestAnimtion = animation;
					}
				});

				return { nearestAnimtion, minDifference };
			}

			const stickOnScroll = this.debounce(() => {
				const { nearestAnimtion: nextAnimation, minDifference } = findNearestAnimation();

				if (nextAnimation.timelineOffset <= this.animation.progress && this.animation.progress <= nextAnimation.timelineOffset + nextAnimation.duration) return;

				if (minDifference >= differenceFactor) {
					this.pauseCurrentAnimation()
					this.currentAnimation = tween({
						targets: this.animation,
						progress: nextAnimation.timelineOffset + nextAnimation.duration || 0,
						easing,
						begin: () => {
							this.isAnimating = true;
						},
						complete: (animation) => {
							this.isAnimating = false;
							animation.remove(this.animation);
						}
					}, constants.DURATION, () => {
						this.seek(this.animation.progress);
					})
				}
			});

			const progress = (!isScrollingDown && (this.animation.progress <= 750 || scroll <= 250)) ? 0 : duration * (scroll / (constants.DURATION * factor));

			this.currentAnimation = tween({
				targets: this.animation,
				progress,
				easing,
				complete: (animation) => {
					animation.remove(this.animation);
				}
			}, constants.DURATION, () => {
				if (this.isAnimating) return;
				if (isScrollingDown) {
					stickOnScroll();
				};
				this.seek(this.animation.progress);
			})
		}

		pauseCurrentAnimation() {
			if (
				!this.currentAnimation ||
				typeof this.currentAnimation !== "object" ||
				!("remove" in this.currentAnimation) ||
				!("pause" in this.currentAnimation)
			) return;
			this.currentAnimation.pause();
			this.currentAnimation.remove(this.animation);
		}

		playOnScroll() {
			window.addEventListener('scroll', this.seekOnScroll.bind(this));

			return this;
		}

		throttle(fn, wait = 100) {
			let time = Date.now();

			return (...args) => {
				if ((time + wait - Date.now()) < 0) {
					fn(...args);
					time = Date.now();
				}
			}
		}

		debounce(fn, wait = 100) {
			let timeout;

			return (...args) => {
				clearTimeout(timeout);
				timeout = setTimeout(() => {
					fn(...args);
				}, wait);
			}
		}

		isScrollingDown(event) {
			const currentScrollPosition = window.scrollY || document.documentElement.scrollTop;
			let isScrollingDown = true;

			if (currentScrollPosition > this.previousScrollPosition) {
				isScrollingDown = true;
				this.scrollDirection = 'down';
			} else {
				isScrollingDown = false;
				this.scrollDirection = 'up';
			}

			this.previousScrollPosition = currentScrollPosition;
			return isScrollingDown;
		}
	}

	class Animation {
		constructor(el, animation, watch = true, autoplay = true) {
			this.el = el;
			this.animation = anime({
				targets: el,
				...animation,
				autoplay: false,
				loop: false,
			})
			this._watch = watch;
			this._watchRatio = 0.5;
			this._rootEl = this.el.parentElement;
			this._threshold = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
			this._rootMargin = '0px';
			this._isIntersecting = false;
			this._observer = null;
			this._autoplay = autoplay;

			this.init();
		}

		get watch() {
			return this._watch;
		}

		get watchRatio() {
			return this._watchRatio;
		}

		set watchRatio(value) {
			this._watchRatio = value;
		}

		get rootEl() {
			return this._rootEl;
		}

		set rootEl(value) {
			this._rootEl = value;
		}

		get threshold() {
			return this._threshold;
		}

		set threshold(value) {
			this._threshold = value;
		}

		get rootMargin() {
			return this._rootMargin;
		}

		set rootMargin(value) {
			this._rootMargin = value;
		}

		get animationState() {
			return this.animation.completed ? constants.animationStatus.COMPLETED : this.animation.paused ? constants.animationStatus.PAUSED : constants.animationStatus.RUNNING;
		}

		get isIntersecting() {
			return this._isIntersecting;
		}

		set isIntersecting(value) {
			this._isIntersecting = value;
		}

		play() {
			this.animation.play();

			return this;
		}

		reverse() {
			this.animation.reverse();
		}

		restart() {
			this.animation.restart();
		}

		reset() {
			this.animation.restart();
			this.animation.pause();
			this.animation.seek(0);
		}

		seek(time) {
			this.animation.seek(time);
		}

		onFinish(callback) {
			if (!callback || typeof callback !== "function") return;
			this.animation.update = (animation) => {
				if (animation.progress === 100) callback(animation);
			}
		}

		onVisible([entry]) {
			const state = this.animationState;
			this.isIntersecting = entry.isIntersecting;

			switch (state) {
				case constants.animationStatus.COMPLETED:
					if (entry.isIntersecting) return;
					this.reset();
					break;
				case constants.animationStatus.PAUSED:
					if (entry.isIntersecting && entry.intersectionRatio >= this.watchRatio) {
						this.play();
					}
					break;
				case constants.animationStatus.RUNNING:
					if (!entry.isIntersecting) {
						this.reset();
					}
					break;
			}
		}

		observe() {
			this._observer = new IntersectionObserver(this.onVisible.bind(this), {
				root: null,
				threshold: this.threshold,
				rootMargin: this.rootMargin,
			});

			this._observer.observe(this.el);
		}

		init() {
			if (this.watch) {
				this.observe();
			} else {
				if (this._autoplay) this.play();
			}
		}

		destroy() {
			this.animation.pause();
			this.animation.seek(0);
			this._animation = null;
			if (this._observer) this._observer.unobserve(this.el);
		}
	}

	class BackgroundAnimation {
		constructor(el, intersectionRatio = 0.6, { rootEl = null, rootMargin = '0px', threshold = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.9, 1] } = {}) {
			this.el = el;
			this._rootEl = rootEl;
			this._rootMargin = rootMargin;
			this._threshold = threshold;
			this._intersectionRatio = intersectionRatio;
			this._currentColor = null;
			this._nextColor = null;
			this._previousEntryRatio = 0;
		}

		onIntersect(entries) {
			entries.forEach(entry => {
				if (entry.isIntersecting && entry.intersectionRatio >= this._intersectionRatio) {
					if (entry.intersectionRatio > this._previousEntryRatio) {
						this._previousEntryRatio = entry.intersectionRatio - 0.2;
						const color = entry.target.dataset.color;

						if (!this._currentColor) {
							this._currentColor = color;
							anime({
								targets: this.el,
								backgroundColor: [color, color],
								duration: 500,
								easing: 'easeOutSine',
							});
						} else {
							anime({
								targets: this.el,
								backgroundColor: color,
								duration: 500,
								easing: 'easeOutSine',
							});
						}
					}
				}
			});
		}

		observe(entries) {
			entries.forEach(entry => {
				const observer = new IntersectionObserver(this.onIntersect.bind(this), {
					rootMargin: this._rootMargin,
					threshold: this._threshold,
				});

				observer.observe(entry);
			})
		}
	}

	const getElementCenter = (el) => {
		const left = el.offsetLeft;
		const top = el.offsetTop;
		const width = el.offsetWidth;
		const height = el.offsetHeight;

		return {
			x: left + width / 2,
			y: top + height / 2,
		}
	}

	const animationSelectors = {
		stepTwo: document.querySelector('[data-animation-step="2"]'),
	}

	const mainImageCenter = getElementCenter(document.querySelector('[data-animation="mainImage"]'));
	const stash = new Map();
	const getStepOneCenter = () => {
		const stepOneCenter = getElementCenter(document.querySelector('[data-animation-step="1"]'));

		return stepOneCenter.y - mainImageCenter.y;
	}

	const animations = {
		fadeInUp: {
			opacity: [0, 1],
			translateY: ["100px", "0px"],
			duration: constants.DURATION,
			easing: constants.EASING,
		},
		fadeInUpHigh: {
			opacity: [0, 1],
			translateY: ["100px", "-100px"],
			duration: constants.DURATION,
			easing: constants.EASING,
		},
		mainImage: {
			0: {
				translateY: 0,
				translateX: 0,
				duration: constants.DURATION / 4,
				easing: constants.EASING,
				scale: 1,
				rotate: 0
			},
			1: {
				translateY: getStepOneCenter(),
				translateX: 0,
				duration: constants.DURATION,
				easing: constants.EASING,
				scale: 1,
				rotate: 0
			},
			2: {
				translateY: function (el) {
					const secondSection = animationSelectors.stepTwo;
					const secondSectionCenter = getElementCenter(secondSection).y;

					return secondSectionCenter + secondSection.offsetHeight / 4;
				},
				translateX: function (el) {
					const secondSection = animationSelectors.stepTwo;
					const rect = secondSection.getBoundingClientRect();

					const secondSectionLeftSide = rect.right - secondSection.offsetWidth / 2;

					return secondSectionLeftSide - getElementCenter(el).x;
				},
				rotate: 45,
				scale: 1.250,
				duration: constants.DURATION,
				easing: constants.EASING,
			},
			3: {
				translateY: function (el) {
					const thirdSection = document.querySelector('[data-animation-step="3"]');
					const thirdSectionCenter = getElementCenter(thirdSection).y;

					return thirdSectionCenter;
				},
				translateX: 0,
				rotate: 0,
				scale: 1.250,
				duration: constants.DURATION,
				easing: constants.EASING,
			},
			4: {
				translateY: function (el) {
					const secondSection = document.querySelector('[data-animation-step="4"]');
					const secondSectionCenter = getElementCenter(secondSection).y;

					return secondSectionCenter - mainImageCenter.y;
				},
				translateX: 0,
				rotate: 0,
				duration: constants.DURATION,
				easing: constants.EASING,
			},
		},
		upFadeOut: {
			opacity: [1, 0.3],
			translateY: [0, -100],
			duration: constants.DURATION,
			easing: constants.EASING,
		},
		downFadeIn: {
			opacity: [0.3, 1],
			translateY: [-100, 0],
			duration: constants.DURATION,
			easing: constants.EASING,
		},
		fadeIn: {
			opacity: [0, 1],
			duration: constants.DURATION / 2,
			easing: constants.EASING,
		},
		fadeOut: {
			opacity: [1, 0],
			duration: constants.DURATION / 2,
			easing: constants.EASING,
		},
		transitToCenter: {
			translateX: 0,
			translateY: function (el) {
				const parent = document.querySelector('#shopify-section-nova_new-features');
				const parentCenter = getElementCenter(parent).y;

				return parentCenter - getElementCenter(el).y;
			},
		},
		scaleDown: {
			scale: 1,
			duration: constants.DURATION / 2,
			rotate: 0,
			easing: constants.EASING,
		}
	}

	const waitForFirstInput = () => {
		return new Promise(resolve => {
			let firstScrollInput = 0;

			const onScroll = () => {
				if (!firstScrollInput) {
					firstScrollInput = window.scrollY;
					return;
				}

				if (Math.abs(window.scrollY - firstScrollInput) > constants.MIN_SCROLL_INPUT) {
					window.removeEventListener('scroll', onScroll);
					resolve();
				}
			}

			window.addEventListener('scroll', onScroll);
		})
	}

	const getImageSwapAnimation = () => {
		const mainImageContainer = document.querySelector('[data-animation="mainImage"]');
		const alternateImg = mainImageContainer.querySelector('img[data-alternate-image]');
		const mainImg = mainImageContainer.querySelector('img:not([data-alternate-image])');
		const currentStash = new Map();
		const previousStash = new Map();

		let current = mainImg;

		return {
			mainToAlternate: () => {
				if (current === alternateImg) return;

				if (currentStash.has(mainImg)) {
					const currentAnimation = currentStash.get(mainImg);
					currentAnimation.play();
				} else {
					const currentAnimation = new Animation(mainImg, animations.fadeOut, false);
					currentStash.set(mainImg, currentAnimation);
				}

				if (previousStash.has(alternateImg)) {
					const previousAnimation = previousStash.get(alternateImg);
					previousAnimation.play();
				} else {
					const previousAnimation = new Animation(alternateImg, animations.fadeIn, false);
					previousStash.set(alternateImg, previousAnimation);
				}

				current = alternateImg;
			},
			alternateToMain: (step) => {
				if (!step || current === mainImg) return;

				if (Number(step) < 3) {
					if (currentStash.has(alternateImg)) {
						const currentAnimation = currentStash.get(alternateImg);
						currentAnimation.play();
					} else {
						const currentAnimation = new Animation(alternateImg, animations.fadeOut, false);
						currentStash.set(alternateImg, currentAnimation);
					}

					if (previousStash.has(mainImg)) {
						const previousAnimation = previousStash.get(mainImg);
						previousAnimation.play();
					} else {
						const previousAnimation = new Animation(mainImg, animations.fadeIn, false);
						previousStash.set(mainImg, previousAnimation);
					}

					current = mainImg;
				}
			}
		}
	}

	const { mainToAlternate, alternateToMain } = getImageSwapAnimation();

	const animateBackground = () => {
		new BackgroundAnimation(document.querySelector('main.main-content'))
			.observe([...document.querySelectorAll('[data-animation="backgroundColor"]')]);
	}

	const getStaggeredAnimations = () => {
		const animatedElements = [...document.querySelectorAll("[data-animation-wait]")];

		return (step) => {
			if (!step) return;

			animatedElements.forEach(el => {
				if (el.dataset.animationWait === step) {
					if (stash.has(el)) {
						const animation = stash.get(el);
						animation.play();
					} else {
						if ("pulse" in el) {
							stash.set(el, new Animation(el, animations.fadeIn, false));
						} else {
							stash.set(el, new Animation(el, animations.fadeInUp));
						}
					}

					if ("pulse" in el) {
						el.pulse();
					}
				} else {
					if (stash.has(el)) {
						stash.get(el).reset();
					}
				}
			});
		}
	}

	const mainAnimation = (tl) => {
		const animateStaggeredSections = getStaggeredAnimations();
		const animatedSections = [...document.querySelectorAll('[data-animation-step]')];
		let previousStep = "0";

		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
					const step = entry.target.dataset.animationStep;

					if (step !== previousStep) {
						previousStep = step;
						animateStaggeredSections(step);
						alternateToMain(step);
						if (step === "3") mainToAlternate();
					}
				}
			});
		}, {
			threshold: 0.6,
		});

		animatedSections.forEach(section => observer.observe(section));
	}

	const animateThirdSection = () => {
		const feature = document.querySelector('.features__informations');
		new Animation(feature, animations.fadeInUpHigh);
	}

	const animateFourthSection = () => {
		const stash = new Map();

		const animatedElements = [...document.querySelectorAll('[data-animation="rotateIn"]')];
		const settings = [...document.querySelectorAll('[data-animation-settings]')];

		animatedElements.forEach((el, i) => {
			if (!stash.has(el)) {
				const animationSettings = JSON.parse(settings[i].textContent);
				const animation = {
					opacity: [0, 1],
					translateY: animationSettings.translateY,
					rotate: [0, animationSettings.rotate],
					translateX: animationSettings.translateX,
					easing: constants.EASING,
					duration: constants.DURATION,
				}
				stash.set(el, new Animation(el, animation));
			}
		});
	}

	animateBackground();

	waitForFirstInput().then(() => {
		const mainTl = new TimelineAnimation(document.querySelector('[data-animation="mainImage"]'))
			.add(animations.mainImage[0])
			.add(animations.mainImage[1])
			.add(animations.mainImage[2])
			.add(animations.mainImage[3])
			.add(animations.scaleDown);

		const harnessAnimation = mainTl.getAnimation(1);
		mainTl.executeAnimation(harnessAnimation, (tl) => {
			return (tl.animation.progress <= 200 && tl.scrollDirection === "down")
		})

		const titleTl = new TimelineAnimation(
			document.querySelector('[data-animation="upFadeOut"]')
		)
			.add(animations.upFadeOut)
			.add(animations.downFadeIn);

		const titleAnimation = titleTl.getAnimation(0);
		titleTl.executeAnimation(titleAnimation, (tl) => {
			return (tl.animation.progress <= 200 && tl.scrollDirection === "down")
		});

		mainAnimation();
		animateThirdSection();
		animateFourthSection();
		[...document.querySelectorAll('[data-animation="fadeInUp"]')].forEach(el => {
			new Animation(el, animations.fadeInUp);
		})
		mainTl.playOnScroll();
		titleTl.playOnScroll();
	})
}
